import Table from "@/common/components/Table";
import { H3 } from "@/common/elements";
import styled from "styled-components";
import {
  addedProductHeaders,
  addedProductKeys,
  getAddedProductTableBody,
  getLogisticViewDetails,
  getRecipentViewDetails,
  getSentDelivery,
} from "./data";
import { KeyValueObj } from "@/common/types";
import { KeyValue } from "@/common/components/key-value";
import { Button } from "@/common/components/button";
import { formatToNaira } from "@/common/utils/general";
import useQuery from "@/hooks/useQuery";
import { DELIVERY_REQUEST_URLS } from "@/common/constants";
import useAuth from "@/hooks/useContexts";

const DeliveryWrapper = styled.div`
  width: 65rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
  background-color: ${({ theme }) => theme.bg.prim_500};
  max-width: 100%;

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.2rem;
  }

  .double-column-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .separate {
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.bg.prim_300};
    margin-bottom: 1rem;
    margin-top: 1rem;
  }

  .control {
    padding: 2rem;
  }

  .amount {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.text.secondary};
  }
`;

export interface IDeliveryRequestPreviewProps {
  deliveryId: string;
  handleAcceptDeliveryFee: () => void;
  handleRejectDeliveryFee: () => void;
  handleConfirmDelivery: () => void;
  accepting: boolean;
  rejecting: boolean;
}

export function DeliveryRequestView({
  deliveryId,
  handleAcceptDeliveryFee,
  handleRejectDeliveryFee,
  accepting,
  rejecting,
  handleConfirmDelivery,
}: IDeliveryRequestPreviewProps) {
  const { data } = useQuery(DELIVERY_REQUEST_URLS + `/${deliveryId}`);

  const deliveryData: KeyValueObj = getSentDelivery(data);
  const { auth } = useAuth();

  const showMarkAsReceived = () => {
    const userId = auth?.user?.company?.id;
    const recipientId = deliveryData?.recipient?.id;
    const deliveryStatus = deliveryData?.delivery?.delivery_status;
    if (
      userId === recipientId &&
      deliveryStatus?.toLowerCase() === "delivered"
    ) {
      return true;
    }
    return false;
  };

  return (
    <DeliveryWrapper>
      <H3>Product Details</H3>

      <Table
        tableHeaders={addedProductHeaders}
        tableData={getAddedProductTableBody(deliveryData?.delivery_items ?? [])}
        tableKeys={addedProductKeys}
        rowLineColor="light"
        removeFilters
      />

      <div className="separate"></div>

      <H3>Recipent Company Details</H3>
      <div className="double-column-wrapper">
        {getRecipentViewDetails(deliveryData?.recipient)?.map(
          (detail: KeyValueObj, i: number) => (
            <KeyValue key={i} title={detail?.title} value={detail?.value} />
          ),
        )}
      </div>

      <div className="separate"></div>

      <H3>Logistic Company Details</H3>
      <div className="double-column-wrapper">
        {getLogisticViewDetails(deliveryData?.company)?.map(
          (detail: KeyValueObj, i: number) => (
            <KeyValue key={i} title={detail?.title} value={detail?.value} />
          ),
        )}
      </div>

      <div className="separate"></div>

      <H3>Delivery Information</H3>
      <div className="double-column-wrapper">
        <KeyValue title={"Pickup Type"} value={deliveryData?.pickup_option} />
        <KeyValue
          title={"Pickup Address"}
          value={deliveryData?.pick_up_address}
        />
        <KeyValue
          title={"Drop off Address"}
          value={deliveryData?.drop_off_address}
        />
        <KeyValue
          title={"Vehicle Needed"}
          value={deliveryData?.vehicle_needed}
        />
        <KeyValue
          title={"Transaction ID"}
          value={deliveryData?.transaction_id}
        />
        <KeyValue
          title={"Delivery Request Status"}
          value={deliveryData?.request_status}
        />
        <KeyValue
          title={"Delivery Status"}
          value={deliveryData?.delivery?.delivery_status ?? "--"}
        />
      </div>

      <div className="separate"></div>

      <H3>Deliery Fees Information</H3>
      <div className="double-column-wrapper">
        <KeyValue
          title={"Total Amount"}
          value={
            <h2 className="amount">
              {formatToNaira(deliveryData?.delivery?.total_amount ?? "0")}
            </h2>
          }
        />
      </div>

      <div className="separate"></div>

      <div className="control flex-col">
        {deliveryData?.request_status?.toLowerCase() === "accepted" &&
        deliveryData?.delivery?.delivery_status?.toLowerCase() === "pending" ? (
          <div className="flex items-center gap-10">
            <Button
              loading={accepting}
              disabled={accepting}
              onClick={handleAcceptDeliveryFee}
            >
              Accept Delivery Charge
            </Button>
            <Button
              secondary
              loading={rejecting}
              disabled={rejecting}
              onClick={handleRejectDeliveryFee}
            >
              Reject Delivery Charge
            </Button>
          </div>
        ) : null}
      </div>
      <div className="control flex-col">
        {showMarkAsReceived() ? (
          <Button loading={accepting} onClick={handleConfirmDelivery}>
            Scan To Recieve
          </Button>
        ) : null}
      </div>
    </DeliveryWrapper>
  );
}
